import {constants as extConsts} from '@wix/document-manager-extensions'
const {APP_WIDGET_TYPE, COMP_TYPES} = extConsts
import type {AppDefinitionId, CompRef, Pointer, PS} from '@wix/document-services-types'
import * as santaCoreUtils from '@wix/santa-core-utils'
import _ from 'lodash'
import component from '../../component/component'
import componentDetectorAPI from '../../componentDetectorAPI/componentDetectorAPI'
import connections from '../../connections/connections'
import constants from '../../constants/constants'
import dataModel from '../../dataModel/dataModel'
import hooks from '../../hooks/hooks'
import clientSpecMapService from '../../tpa/services/clientSpecMapService'
import tpaUtils from '../../tpa/utils/tpaUtils'
import refComponent from '../../refComponent/refComponent'
import appControllerUtils from '../../appControllerData/appControllerUtils'
import experiment from 'experiment'

const WIX_CODE_APP_DEF_ID = '2f70e2b4-ff36-472e-bdb9-ce393b13669e'

function addDependantApps(ps: PS, apps) {
    const refreshedAppsDataWithBuilderWidgets = clientSpecMapService.getAppsDataWithPredicate(ps, csm =>
        _.filter(csm, appData => _.includes(apps, appData.appDefinitionId) && appHasBuilderWidgets(appData))
    )
    if (_.includes(apps, 'dataBinding') || !_.isEmpty(refreshedAppsDataWithBuilderWidgets)) {
        const wixCodeAppDefId = getWixCodeAppDefIdIfExists(ps)
        if (wixCodeAppDefId && !_.includes(apps, wixCodeAppDefId)) {
            apps.push(wixCodeAppDefId)
        }
    }
    return apps
}

function getWixCodeAppDefIdIfExists(ps: PS) {
    let wixCodeAppData
    if (experiment.isOpen('dm_cloudSiteExtensionMigrationDev')) {
        wixCodeAppData = clientSpecMapService.getAppDataByAppDefinitionId(ps, WIX_CODE_APP_DEF_ID)
        if (wixCodeAppData) {
            return wixCodeAppData.appDefinitionId
        }
    }
    wixCodeAppData = clientSpecMapService.filterAppsDataByType(ps, 'siteextension')[0]
    return wixCodeAppData ? wixCodeAppData.appDefinitionId : null
}

function hasAppsWithViewerScript(ps: PS) {
    return clientSpecMapService.getAppsDataWithPredicate(ps, csm =>
        _.some(
            csm,
            appData =>
                _.get(appData, ['appFields', 'platform', 'viewerScriptUrl']) ||
                (experiment.isOpen('dm_cloudSiteExtensionMigrationDev') &&
                    _.get(appData, 'appDefinitionId') === WIX_CODE_APP_DEF_ID) ||
                _.get(appData, 'type') === 'siteextension'
        )
    )
}

function isBlocksWidgetComponent(widget) {
    return _.has(widget, ['componentFields', 'appStudioFields'])
}

function isBlocksWidgetRef(ps: PS, compPointer: Pointer) {
    if (component.getType(ps, compPointer) !== COMP_TYPES.REF_TYPE) {
        return false
    }
    const data = dataModel.getDataItem(ps, compPointer)
    if (!data) {
        return false
    }

    const {appDefinitionId, widgetId} = data
    if (!appDefinitionId) {
        return false
    }
    const {widgets} = clientSpecMapService.getAppDataByAppDefinitionId(ps, appDefinitionId) ?? {}
    const widgetComponent = widgets?.[widgetId]
    return isBlocksWidgetComponent(widgetComponent)
}

function isBlocksWidgetInnerComponent(ps: PS, componentPointer: CompRef) {
    if (!refComponent.isReferredComponent(ps, componentPointer)) {
        return false
    }
    const refHost = refComponent.getRefHostCompPointer(ps, componentPointer)
    // TODO remove this check (https://jira.wixpress.com/browse/WBL-5407)
    // in the classic editor when we add new widget to the stage, there is a corruption in mobile data,
    // until we open a MOBILE editor (inner component exists but ref host is not)
    // this function actually only indicated the problem, it's not causing it
    // we just add this check so that this code will not fail in case of data corruption
    // thread: https://wix.slack.com/archives/C01M1AH0H3R/p1680697033202239?thread_ts=1680425393.905679&cid=C01M1AH0H3R
    if (!component.isExist(ps, refHost)) {
        return false
    }
    return isBlocksWidgetRef(ps, refHost)
}

function appHasBuilderWidgets(appData) {
    const widgets = _.values(_.get(appData, 'widgets', {}))
    return _.some(widgets, isBlocksWidgetComponent)
}

function isBlocksAppWidget(ps: PS, compPointer: Pointer) {
    if (component.getType(ps, compPointer) !== APP_WIDGET_TYPE) {
        return false
    }
    const parentCompPointer = component.getContainer(ps, compPointer)
    return isBlocksWidgetRef(ps, parentCompPointer)
}

function addResetRuntimeOverridesForRepeatedItemTemplate(ps: PS, componentPointer: Pointer) {
    const hasRepeaterAncestor = component
        .getAncestorsFromFull(ps, componentPointer)
        .some(ancestor => ps.extensionAPI.componentDefinition.isRepeater(component.getType(ps, ancestor)))

    if (hasRepeaterAncestor) {
        return {resetRuntime: true}
    }
}

function getCompsIdsToReset(ps: PS, componentPointer: Pointer) {
    if (ps.extensionAPI.componentDefinition.isRepeater(component.getType(ps, componentPointer))) {
        const compsIdsToReset = _.map(component.getChildren(ps, componentPointer, true), 'id').concat([
            componentPointer.id
        ])
        return {compsIdsToReset, resetRuntime: true}
    }
    const compAncestors = component.getAncestors(ps, componentPointer)
    const repeaterAncestorPointer = compAncestors.find(
        ancestor =>
            component.isExist(ps, ancestor) && // ref component ancestor on mobile is not available until mobile editor create it
            ps.extensionAPI.componentDefinition.isRepeater(component.getType(ps, ancestor))
    )
    /*can be temple item or repeated item*/
    if (repeaterAncestorPointer) {
        const templateId = santaCoreUtils.displayedOnlyStructureUtil.getRepeaterTemplateId(componentPointer.id)
        const repeaterData = dataModel.getRuntimeDataItem(ps, repeaterAncestorPointer)
        if (repeaterData) {
            const items = _.map(repeaterData.items, itemId =>
                santaCoreUtils.displayedOnlyStructureUtil.getUniqueDisplayedId(templateId, itemId)
            )
            return {compsIdsToReset: items.concat(repeaterAncestorPointer.id), resetRuntime: true}
        }
    }
    return {compsIdsToReset: [componentPointer.id], resetRuntime: true}
}

function getAppsPartsToRefresh(ps: PS, apps) {
    let controllersToRefresh = apps.reduce(
        (flatControllers, appDefinitionId: AppDefinitionId) =>
            flatControllers.concat(
                getAppControllersOnPage(ps, appDefinitionId, ps.siteAPI.getPrimaryPageId()),
                getAppControllersOnPage(ps, appDefinitionId, constants.MASTER_PAGE_ID)
            ),
        []
    )
    controllersToRefresh = _.uniqBy(controllersToRefresh, 'id')
    const compsIdsToReset = controllersToRefresh.reduce((flatComponents, controllerRef) => {
        if (tpaUtils.isTpaByCompType(ps, component.getType(ps, controllerRef))) {
            flatComponents.push(controllerRef)
        }
        return flatComponents.concat(_.map(getControllerConnectedComponents(ps, controllerRef), 'id'))
    }, [])

    const controllersDataItemsToRefresh = _.map(controllersToRefresh, componentPointer => {
        const controllerData = dataModel.getDataItem(ps, componentPointer)
        return controllerData.id
    })
    return {apps, compsIdsToReset, controllersToRefresh: controllersDataItemsToRefresh, resetRuntime: true}
}

function getAppControllersOnPage(ps: PS, appDefId: string, pageId: string) {
    const isControllerOnPage = compRef => {
        const compType = component.getType(ps, compRef)
        const compData = component.data.get(ps, compRef) || {}

        const controllerAppDefId =
            (connections.isOOIController(ps, compType) && compData.appDefinitionId) ||
            (connections.isControllerType(compType) && appControllerUtils.getControllerAppDefinitionId(compData))

        return controllerAppDefId === appDefId
    }
    return componentDetectorAPI.getAllComponents(ps, pageId, compRef => isControllerOnPage(compRef))
}

function getControllerConnectedComponents(ps: PS, controllerRef: Pointer) {
    return connections.getConnectedComponents(ps, controllerRef)
}

export default {
    addDependantApps,
    getWixCodeAppDefIdIfExists,
    hasAppsWithViewerScript,
    isBlocksAppWidget,
    isBlocksWidgetRef,
    isBlocksWidgetInnerComponent,
    optionsModifierSelfRefresh: getAppsPartsToRefresh,
    optionsModifiersAllApps: {
        [hooks.HOOKS.DATA.UPDATE_AFTER]: getCompsIdsToReset,
        [hooks.HOOKS.PROPERTIES.UPDATE_AFTER]: getCompsIdsToReset,
        [hooks.HOOKS.CONNECTION.AFTER_DISCONNECT]: getCompsIdsToReset,
        [hooks.HOOKS.DATA.AFTER_UPDATE_CONNECTIONS]: getCompsIdsToReset,
        [hooks.HOOKS.CHANGE_PARENT.AFTER]: getCompsIdsToReset
    },
    optionsModifiers: {
        dataBinding: addResetRuntimeOverridesForRepeatedItemTemplate
    }
}
